import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { portalApi, type PortalBookingDto, type PortalEnquiryDto } from '@/api/portal'
import { CalendarDays, Plus, Clock, CheckCircle2, XCircle, RefreshCw, MessageSquare, Users, Star } from 'lucide-react'
import { Button } from '@/components/ui/button'
import BookingEnquiryModal from './BookingEnquiryModal'
import FeedbackModal from './FeedbackModal'
import { guardPreview } from '@/lib/previewMode'
import { usePortalMoney } from '@/hooks/useMoney'
import { usePortalDates } from '@/hooks/useDates'
import { usePortalPermissions } from '@/hooks/usePortalPermissions'

function BookingStatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    Booked:    'bg-blue-100 text-blue-700',
    Completed: 'bg-green-100 text-green-700',
    Cancelled: 'bg-red-100 text-red-600',
    Rejected:  'bg-amber-100 text-amber-700',
  }
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${map[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  )
}

function PaymentBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-gray-300">—</span>
  const map: Record<string, string> = {
    Unpaid:        'bg-red-100 text-red-700',
    PartiallyPaid: 'bg-amber-100 text-amber-700',
    Paid:          'bg-green-100 text-green-700',
    Void:          'bg-gray-100 text-gray-500',
  }
  const labels: Record<string, string> = {
    Unpaid: 'Unpaid', PartiallyPaid: 'Part paid', Paid: 'Paid', Void: 'Void',
  }
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${map[status] ?? 'bg-gray-100 text-gray-500'}`}>
      {labels[status] ?? status}
    </span>
  )
}

const ENQUIRY_STATUS = {
  Pending: {
    label: 'Pending',
    icon: Clock,
    cls: 'bg-amber-100 text-amber-700',
    description: 'Your request has been received and is awaiting a response.',
  },
  Acknowledged: {
    label: 'Acknowledged',
    icon: CheckCircle2,
    cls: 'bg-blue-100 text-blue-700',
    description: 'The training company has seen your request and is looking into it.',
  },
  Converted: {
    label: 'Booking confirmed',
    icon: CheckCircle2,
    cls: 'bg-green-100 text-green-700',
    description: 'Your request has been confirmed — check the Upcoming tab for your booking.',
  },
  Declined: {
    label: 'Declined',
    icon: XCircle,
    cls: 'bg-gray-100 text-gray-500',
    description: 'Unfortunately this request could not be accommodated.',
  },
}

function EnquiryCard({ enquiry }: { enquiry: PortalEnquiryDto }) {
  const { formatDate, formatDateTime } = usePortalDates()
  const fmtDate = (d: string) => {
    return formatDate(d + 'T00:00:00', {
      day: 'numeric', month: 'short', year: 'numeric',
    })
  }
  const fmtDateTime = (d: string) => {
    return formatDateTime(d, {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })
  }
  const cfg = ENQUIRY_STATUS[enquiry.status] ?? ENQUIRY_STATUS.Pending
  const Icon = cfg.icon

  return (
    <div className={`border rounded-xl overflow-hidden ${
      enquiry.status === 'Pending' ? 'border-amber-200 bg-amber-50/40' :
      enquiry.status === 'Acknowledged' ? 'border-blue-200 bg-blue-50/30' :
      enquiry.status === 'Converted' ? 'border-green-200 bg-green-50/30' :
      'border-gray-200 bg-gray-50/50'
    }`}>
      <div className="px-4 py-4">
        {/* Top row: course + status */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <p className="font-semibold text-gray-900">{enquiry.courseName}</p>
            <div className="flex flex-wrap gap-3 mt-0.5 text-xs text-gray-500">
              {enquiry.preferredDate && (
                <span>Preferred: {fmtDate(enquiry.preferredDate)}</span>
              )}
              <span>{enquiry.places} {enquiry.places === 1 ? 'attendee' : 'attendees'}</span>
              {enquiry.requestedStaffNames?.length > 0 && (
                <span>Staff: {enquiry.requestedStaffNames.join(', ')}</span>
              )}
              <span>Requested {fmtDateTime(enquiry.createdAt)}</span>
            </div>
          </div>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold shrink-0 ${cfg.cls}`}>
            <Icon size={12} />
            {cfg.label}
          </span>
        </div>

        {/* Status description */}
        <p className="text-sm text-gray-500 mt-2">{cfg.description}</p>

        {/* Original message */}
        {enquiry.message && (
          <div className="mt-3 flex gap-2 text-sm text-gray-600 bg-white/70 border border-gray-200 rounded-lg px-3 py-2">
            <MessageSquare size={13} className="text-gray-400 mt-0.5 shrink-0" />
            <p className="whitespace-pre-wrap">{enquiry.message}</p>
          </div>
        )}

        {/* Admin notes (shown when acknowledged or declined) */}
        {enquiry.adminNotes && (
          <div className="mt-2 text-sm">
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">Message from training company</span>
            <p className="mt-1 text-gray-700 bg-white/70 border border-gray-200 rounded-lg px-3 py-2">
              {enquiry.adminNotes}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

function EditAttendeesModal({ booking, onClose }: { booking: PortalBookingDto; onClose: () => void }) {
  const { formatDate } = usePortalDates()
  const queryClient = useQueryClient()

  const { data: staff = [], isLoading: staffLoading } = useQuery({
    queryKey: ['portal-staff'],
    queryFn: () => portalApi.getStaff().then(r => r.data),
  })

  const { data: current = [], isLoading: attendeesLoading } = useQuery({
    queryKey: ['portal-booking-attendees', booking.id],
    queryFn: () => portalApi.getBookingAttendees(booking.id).then(r => r.data),
  })

  const [selected, setSelected] = useState<Set<string> | null>(null)

  // Initialise selection once current attendees load
  if (current.length >= 0 && selected === null && !attendeesLoading) {
    setSelected(new Set(current.map(a => a.staffMemberId)))
  }

  const mutation = useMutation({
    mutationFn: () => portalApi.updateBookingAttendees(booking.id, [...(selected ?? [])]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-bookings'] })
      queryClient.invalidateQueries({ queryKey: ['portal-booking-attendees', booking.id] })
      onClose()
    },
  })

  const activeStaff = staff.filter(s => s.isActive)
  const isLoading = staffLoading || attendeesLoading
  const sel = selected ?? new Set<string>()

  const toggle = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col max-h-[80vh]">
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">Edit attendees</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {booking.courseName} · {formatDate(booking.bookingDate + 'T00:00:00', { day: 'numeric', month: 'short', year: 'numeric' })}
          </p>
          <p className="text-xs text-gray-400 mt-1">{booking.places} place{booking.places !== 1 ? 's' : ''} booked</p>
        </div>

        {/* Staff list */}
        <div className="overflow-y-auto flex-1 px-4 py-3">
          {isLoading ? (
            <p className="text-sm text-gray-400 py-4 text-center">Loading…</p>
          ) : activeStaff.length === 0 ? (
            <p className="text-sm text-gray-400 py-4 text-center">No active staff members found.</p>
          ) : (
            <div className="space-y-1">
              {activeStaff.map(s => (
                <label key={s.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                  <input
                    type="checkbox"
                    checked={sel.has(s.id)}
                    onChange={() => toggle(s.id)}
                    className="w-4 h-4 rounded border-gray-300 text-[var(--brand-primary)] focus:ring-[var(--brand-primary)] cursor-pointer"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{s.firstName} {s.lastName}</p>
                    <p className="text-xs text-gray-400 truncate">{s.email}</p>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
          <p className="text-sm text-gray-500">{sel.size} selected</p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
            <Button size="sm" onClick={() => mutation.mutate()} disabled={mutation.isPending || isLoading}>
              {mutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

function BookingsTable({ bookings, emptyMessage, showInvoices, onEditAttendees, onFeedback }: {
  bookings: PortalBookingDto[]
  emptyMessage: string
  showInvoices: boolean
  onEditAttendees?: (b: PortalBookingDto) => void
  onFeedback?: (b: PortalBookingDto) => void
}) {
  const { formatDate } = usePortalDates()
  const fmtDate = (d: string) => {
    return formatDate(d + 'T00:00:00', {
      day: 'numeric', month: 'short', year: 'numeric',
    })
  }
  const { fmt: fmtMoney } = usePortalMoney()
  if (bookings.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400 text-sm bg-white border border-gray-200 rounded-xl">
        {emptyMessage}
      </div>
    )
  }

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
      {/* Mobile: card list */}
      <div className="sm:hidden divide-y divide-gray-100">
        {bookings.map(b => (
          <div key={b.id} className="px-4 py-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium text-gray-900 truncate">{b.courseName}</p>
                {b.courseCode && <p className="text-xs text-gray-400">{b.courseCode}</p>}
              </div>
              <BookingStatusBadge status={b.status} />
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-gray-500">
              <span>{fmtDate(b.bookingDate)}</span>
              <span>{b.places} {b.places === 1 ? 'place' : 'places'}</span>
              {showInvoices && b.invoiceNumber && (
                <span>
                  {b.invoiceNumber}
                  {b.invoiceAmount != null && <span className="ml-1 text-gray-400">{fmtMoney(b.invoiceAmount)}</span>}
                </span>
              )}
              {showInvoices && b.paymentStatus && <PaymentBadge status={b.paymentStatus} />}
            </div>
            <div className="flex gap-3 mt-2">
              {b.status === 'Booked' && onEditAttendees && (
                <button
                  onClick={() => onEditAttendees(b)}
                  className="inline-flex items-center gap-1.5 text-xs text-[var(--brand-primary)] hover:opacity-80 font-medium cursor-pointer"
                >
                  <Users size={12} /> Edit attendees
                </button>
              )}
              {b.status === 'Completed' && onFeedback && (
                <button
                  onClick={() => onFeedback(b)}
                  className={`inline-flex items-center gap-1.5 text-xs font-medium cursor-pointer ${
                    b.hasFeedback ? 'text-amber-500 hover:text-amber-700' : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  <Star size={12} className={b.hasFeedback ? 'fill-amber-400' : ''} />
                  {b.hasFeedback ? `Rated ${b.feedbackRating}/10` : 'Rate this session'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop: table */}
      <table className="hidden sm:table w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Course</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Attendees</th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
            {showInvoices && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Invoice</th>}
            {showInvoices && <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Payment</th>}
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {bookings.map(b => (
            <tr key={b.id} className="hover:bg-slate-50 transition-colors">
              <td className="px-4 py-3">
                <p className="font-medium text-gray-900">{b.courseName}</p>
                {b.courseCode && <p className="text-xs text-gray-400">{b.courseCode}</p>}
              </td>
              <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{fmtDate(b.bookingDate)}</td>
              <td className="px-4 py-3 text-gray-600 tabular-nums">{b.places}</td>
              <td className="px-4 py-3">
                <BookingStatusBadge status={b.status} />
              </td>
              {showInvoices && <>
              <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                {b.invoiceNumber ? (
                  <span>
                    {b.invoiceNumber}
                    {b.invoiceAmount != null && (
                      <span className="ml-1 text-gray-400">{fmtMoney(b.invoiceAmount)}</span>
                    )}
                  </span>
                ) : (
                  <span className="text-gray-300">—</span>
                )}
              </td>
              <td className="px-4 py-3">
                <PaymentBadge status={b.paymentStatus} />
              </td>
              </>}
              <td className="px-4 py-3 text-right">
                <div className="flex items-center justify-end gap-3">
                  {b.status === 'Booked' && onEditAttendees && (
                    <button
                      onClick={() => onEditAttendees(b)}
                      className="inline-flex items-center gap-1.5 text-xs text-[var(--brand-primary)] hover:opacity-80 font-medium cursor-pointer whitespace-nowrap"
                    >
                      <Users size={13} /> Edit attendees
                    </button>
                  )}
                  {b.status === 'Completed' && onFeedback && (
                    <button
                      onClick={() => onFeedback(b)}
                      className={`inline-flex items-center gap-1.5 text-xs font-medium cursor-pointer whitespace-nowrap ${
                        b.hasFeedback
                          ? 'text-amber-500 hover:text-amber-700'
                          : 'text-gray-400 hover:text-gray-600'
                      }`}
                    >
                      <Star size={13} className={b.hasFeedback ? 'fill-amber-400' : ''} />
                      {b.hasFeedback ? `${b.feedbackRating}/10` : 'Rate'}
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function BookingsPage() {
  const [tab, setTab] = useState<'upcoming' | 'past' | 'enquiries'>('upcoming')
  const [showEnquiry, setShowEnquiry] = useState(false)
  const [editingBooking, setEditingBooking] = useState<PortalBookingDto | null>(null)
  const [feedbackBooking, setFeedbackBooking] = useState<PortalBookingDto | null>(null)
  const permissions = usePortalPermissions()

  const { data, isLoading: bookingsLoading } = useQuery({
    queryKey: ['portal-bookings'],
    queryFn: () => portalApi.getBookings().then(r => r.data),
  })

  const { data: enquiries = [], isLoading: enquiriesLoading } = useQuery({
    queryKey: ['portal-enquiries'],
    queryFn: () => portalApi.getEnquiries().then(r => r.data),
    enabled: permissions.bookingRequests,
  })

  const isLoading = bookingsLoading || enquiriesLoading
  if (isLoading) return <p className="text-gray-500">Loading…</p>
  if (!data) return null

  const pendingEnquiries = enquiries.filter(e => e.status === 'Pending')

  return (
    <div className="space-y-6">
      {showEnquiry && (
        <BookingEnquiryModal
          onClose={() => setShowEnquiry(false)}
          onSubmitted={() => setTab('enquiries')}
        />
      )}
      {editingBooking && (
        <EditAttendeesModal
          booking={editingBooking}
          onClose={() => setEditingBooking(null)}
        />
      )}
      {feedbackBooking && (
        <FeedbackModal
          booking={feedbackBooking}
          onClose={() => setFeedbackBooking(null)}
        />
      )}

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <CalendarDays size={22} className="text-[var(--brand-primary)]" />
            Bookings
          </h1>
          <p className="text-sm text-gray-500 mt-1">Your organisation's course bookings</p>
        </div>
        {permissions.bookingRequests && (
          <Button onClick={() => setShowEnquiry(true)} className="gap-2 shrink-0">
            <Plus size={16} /> Request a booking
          </Button>
        )}
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg overflow-x-auto w-full sm:w-fit">
        <button
          onClick={() => setTab('upcoming')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${
            tab === 'upcoming' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Upcoming ({data.upcoming.length})
        </button>
        <button
          onClick={() => setTab('past')}
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${
            tab === 'past' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Past ({data.past.length})
        </button>
        {permissions.bookingRequests && (
          <button
            onClick={() => setTab('enquiries')}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer flex items-center gap-2 ${
              tab === 'enquiries' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <RefreshCw size={13} />
            Enquiries ({enquiries.length})
            {pendingEnquiries.length > 0 && (
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-500 text-white text-xs font-bold">
                {pendingEnquiries.length}
              </span>
            )}
          </button>
        )}
      </div>

      {tab === 'upcoming' && (
        <BookingsTable bookings={data.upcoming} emptyMessage="No upcoming bookings." showInvoices={permissions.invoices}
          onEditAttendees={permissions.assignAttendees ? (b => { if (!guardPreview()) setEditingBooking(b) }) : undefined} />
      )}
      {tab === 'past' && (
        <BookingsTable bookings={data.past} emptyMessage="No past bookings." showInvoices={permissions.invoices}
          onEditAttendees={permissions.assignAttendees ? (b => { if (!guardPreview()) setEditingBooking(b) }) : undefined}
          onFeedback={permissions.feedback ? (b => { if (!guardPreview()) setFeedbackBooking(b) }) : undefined} />
      )}
      {tab === 'enquiries' && permissions.bookingRequests && (
        <div className="space-y-3">
          {enquiries.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm bg-white border border-gray-200 rounded-xl">
              No booking requests yet. Use "Request a booking" to get started.
            </div>
          ) : (
            enquiries.map(e => <EnquiryCard key={e.id} enquiry={e} />)
          )}
        </div>
      )}
    </div>
  )
}
